import type { SearchParams } from '@/types/api';
import type { EntityOptionResponse } from '@/types/entityOption';

export interface HrOrgFilterState {
  departmentId: string;
  designationId: string;
  gradeId: string;
  businessUnitId: string;
}

export const EMPTY_HR_ORG_FILTERS: HrOrgFilterState = {
  departmentId: '',
  designationId: '',
  gradeId: '',
  businessUnitId: '',
};

export function hasHrOrgFilter(filters: HrOrgFilterState): boolean {
  return !!(
    filters.departmentId ||
    filters.designationId ||
    filters.gradeId ||
    filters.businessUnitId
  );
}

export function buildHrEmployeeSearchParams(
  orgFilters: HrOrgFilterState,
  extra?: Partial<SearchParams>,
): SearchParams {
  return {
    page: 0,
    size: 500,
    activeOnly: true,
    sortBy: 'firstName',
    sortDirection: 'ASC',
    departmentId: orgFilters.departmentId ? Number(orgFilters.departmentId) : undefined,
    designationId: orgFilters.designationId ? Number(orgFilters.designationId) : undefined,
    gradeId: orgFilters.gradeId ? Number(orgFilters.gradeId) : undefined,
    businessUnitId: orgFilters.businessUnitId ? Number(orgFilters.businessUnitId) : undefined,
    ...extra,
  };
}

/** Org filters for list/calendar APIs (without employee selection). */
export function orgFiltersToSearchParams(orgFilters: HrOrgFilterState): Partial<SearchParams> {
  return {
    departmentId: orgFilters.departmentId ? Number(orgFilters.departmentId) : undefined,
    designationId: orgFilters.designationId ? Number(orgFilters.designationId) : undefined,
    gradeId: orgFilters.gradeId ? Number(orgFilters.gradeId) : undefined,
    businessUnitId: orgFilters.businessUnitId ? Number(orgFilters.businessUnitId) : undefined,
  };
}

export function parseEmployeeIdStrings(values: string[]): number[] {
  return values.map(Number).filter((id) => !Number.isNaN(id) && id > 0);
}

/**
 * Employees shown in calendar/list views:
 * - explicit multiselect when set
 * - otherwise all employees matching org filters
 * - otherwise undefined (no employee restriction)
 */
export function resolveEffectiveEmployeeIds(
  filteredEmployees: EntityOptionResponse[],
  selectedEmployeeIds: string[],
  orgFilters: HrOrgFilterState,
): number[] | undefined {
  if (selectedEmployeeIds.length > 0) {
    const selected = parseEmployeeIdStrings(selectedEmployeeIds);
    return selected.length > 0 ? selected : [];
  }
  if (hasHrOrgFilter(orgFilters)) {
    return filteredEmployees.map((e) => e.id);
  }
  return undefined;
}

export function employeeIdsToSearchParams(
  employeeIds: number[] | undefined,
): Pick<SearchParams, 'employeeIds' | 'employeeId'> {
  if (employeeIds && employeeIds.length === 1) {
    return { employeeId: employeeIds[0] };
  }
  if (employeeIds && employeeIds.length > 1) {
    return { employeeIds };
  }
  return {};
}
